import { BadgeOption } from "../constants/submissionSummaryOptions";

/**
 * Route-via values for the Submission Summary modal key dropdown.
 */
export enum SubmissionSummaryRouteVia {
  MISSING_PRESCRIPTION = "Missing Prescription",
  FORM_ONLY = "Form Only",
  MEDICAL_BENEFIT = "Medical Benefit",
  SELF_PAY = "Self Pay",
  CMM = "CMM",
  EVICORE = "Evicore",
  EOC_ID = "EOC ID",
  OTHERS = "Others",
}

/**
 * Key text-field behavior for each route-via option.
 */
export enum SubmissionSummaryRouteViaKeyFieldMode {
  GRAYED_OUT = "grayed_out",
  OPTIONAL = "optional",
  MANDATORY = "mandatory",
}

/**
 * Key text-field behavior mapped to each route-via option.
 */
export const SUBMISSION_SUMMARY_ROUTE_VIA_KEY_FIELD_MODE: Record<
  SubmissionSummaryRouteVia,
  SubmissionSummaryRouteViaKeyFieldMode
> = {
  [SubmissionSummaryRouteVia.MISSING_PRESCRIPTION]:
    SubmissionSummaryRouteViaKeyFieldMode.GRAYED_OUT,
  [SubmissionSummaryRouteVia.FORM_ONLY]:
    SubmissionSummaryRouteViaKeyFieldMode.OPTIONAL,
  [SubmissionSummaryRouteVia.MEDICAL_BENEFIT]:
    SubmissionSummaryRouteViaKeyFieldMode.GRAYED_OUT,
  [SubmissionSummaryRouteVia.SELF_PAY]:
    SubmissionSummaryRouteViaKeyFieldMode.GRAYED_OUT,
  [SubmissionSummaryRouteVia.CMM]:
    SubmissionSummaryRouteViaKeyFieldMode.MANDATORY,
  [SubmissionSummaryRouteVia.EVICORE]:
    SubmissionSummaryRouteViaKeyFieldMode.MANDATORY,
  [SubmissionSummaryRouteVia.EOC_ID]:
    SubmissionSummaryRouteViaKeyFieldMode.MANDATORY,
  [SubmissionSummaryRouteVia.OTHERS]:
    SubmissionSummaryRouteViaKeyFieldMode.MANDATORY,
};

/**
 * Display labels for {@link SubmissionSummaryRouteVia}.
 */
export const SubmissionSummaryRouteViaLabels: Record<
  SubmissionSummaryRouteVia,
  string
> = {
  [SubmissionSummaryRouteVia.MISSING_PRESCRIPTION]: "Missing Prescription",
  [SubmissionSummaryRouteVia.FORM_ONLY]: "Form Only",
  [SubmissionSummaryRouteVia.MEDICAL_BENEFIT]: "Medical Benefit",
  [SubmissionSummaryRouteVia.SELF_PAY]: "Self Pay",
  [SubmissionSummaryRouteVia.CMM]: "CMM",
  [SubmissionSummaryRouteVia.EVICORE]: "Evicore",
  [SubmissionSummaryRouteVia.EOC_ID]: "EOC ID",
  [SubmissionSummaryRouteVia.OTHERS]: "Others",
};

/**
 * Badge dropdown options for route via in the Submission Summary modal.
 */
export const SUBMISSION_SUMMARY_ROUTE_VIA_OPTIONS: BadgeOption[] = [
  SubmissionSummaryRouteVia.CMM,
  SubmissionSummaryRouteVia.EVICORE,
  SubmissionSummaryRouteVia.EOC_ID,
  SubmissionSummaryRouteVia.MISSING_PRESCRIPTION,
  SubmissionSummaryRouteVia.FORM_ONLY,
  SubmissionSummaryRouteVia.MEDICAL_BENEFIT,
  SubmissionSummaryRouteVia.SELF_PAY,
  SubmissionSummaryRouteVia.OTHERS,
].map((routeVia) => ({
  id: routeVia,
  text: SubmissionSummaryRouteViaLabels[routeVia],
  textColor: "black",
  bgColor: "white",
}));

/**
 * Resolves a stored `route_via` value to a {@link SubmissionSummaryRouteVia} enum member.
 *
 * @param raw - Raw route-via value from order data
 * @returns Matching enum value, or `SubmissionSummaryRouteVia.CMM` when unset or unrecognized
 */
export const resolveSubmissionSummaryRouteVia = (
  raw: string | null | undefined,
): SubmissionSummaryRouteVia => {
  if (!raw) {
    return SubmissionSummaryRouteVia.CMM;
  }

  const normalized = raw.trim().toLowerCase();
  if (normalized === "prompt pa") {
    return SubmissionSummaryRouteVia.EOC_ID;
  }

  const matched = Object.values(SubmissionSummaryRouteVia).find(
    (value) => value.toLowerCase() === normalized,
  );

  return matched ?? SubmissionSummaryRouteVia.CMM;
};

const STATIC_COVERMYMEDS_ID_BY_ROUTE_VIA: Partial<
  Record<SubmissionSummaryRouteVia, string>
> = {
  [SubmissionSummaryRouteVia.MISSING_PRESCRIPTION]:
    SubmissionSummaryRouteViaLabels[SubmissionSummaryRouteVia.MISSING_PRESCRIPTION],
  [SubmissionSummaryRouteVia.MEDICAL_BENEFIT]:
    SubmissionSummaryRouteViaLabels[SubmissionSummaryRouteVia.MEDICAL_BENEFIT],
  [SubmissionSummaryRouteVia.SELF_PAY]:
    SubmissionSummaryRouteViaLabels[SubmissionSummaryRouteVia.SELF_PAY],
};

const PREFIXED_COVERMYMEDS_ID_BY_ROUTE_VIA: Partial<
  Record<SubmissionSummaryRouteVia, string>
> = {
  [SubmissionSummaryRouteVia.FORM_ONLY]: "Form Only - ",
  [SubmissionSummaryRouteVia.EVICORE]: "Evicore - ",
  [SubmissionSummaryRouteVia.EOC_ID]: "EOC ID - ",
};

/** Legacy key prefixes from before the Prompt PA → EOC ID rename. */
const LEGACY_ROUTE_VIA_KEY_PREFIXES = ["Prompt PA - "];

/**
 * Removes a known route-via prefix from a stored covermymed_id value.
 *
 * @param key - Stored or user-entered key value
 * @returns Raw key value without a route-via prefix
 */
export const stripRouteViaPrefixFromKey = (key: string): string => {
  let trimmedKey = key.trim();

  let didStripPrefix = true;
  while (didStripPrefix) {
    didStripPrefix = false;
    for (const prefix of [
      ...Object.values(PREFIXED_COVERMYMEDS_ID_BY_ROUTE_VIA),
      ...LEGACY_ROUTE_VIA_KEY_PREFIXES,
    ]) {
      if (prefix && trimmedKey.startsWith(prefix)) {
        trimmedKey = trimmedKey.slice(prefix.length).trim();
        didStripPrefix = true;
        break;
      }
    }
  }

  return trimmedKey;
};

/**
 * Builds the `covermymed_id` payload value for a selected route via.
 *
 * @param routeVia - Selected route-via option
 * @param rawValue - Raw key value shown in the text field
 * @returns Mapped covermymed_id value for the submit payload
 */
export const buildCoverMyMedsIdForRouteVia = (
  routeVia: SubmissionSummaryRouteVia,
  rawValue: string,
): string => {
  const staticValue = STATIC_COVERMYMEDS_ID_BY_ROUTE_VIA[routeVia];
  if (staticValue) {
    return staticValue;
  }

  if (
    routeVia === SubmissionSummaryRouteVia.CMM ||
    routeVia === SubmissionSummaryRouteVia.OTHERS
  ) {
    return rawValue;
  }

  const prefix = PREFIXED_COVERMYMEDS_ID_BY_ROUTE_VIA[routeVia];
  if (prefix) {
    const normalizedValue = stripRouteViaPrefixFromKey(rawValue);
    return normalizedValue ? `${prefix}${normalizedValue}` : prefix.trim();
  }

  return rawValue;
};

/**
 * Returns whether the key text field should be disabled for a route via.
 *
 * @param routeVia - Selected route-via option
 * @returns True when the field is grayed out
 */
export const isSubmissionSummaryKeyFieldDisabled = (
  routeVia: SubmissionSummaryRouteVia,
): boolean =>
  SUBMISSION_SUMMARY_ROUTE_VIA_KEY_FIELD_MODE[routeVia] ===
  SubmissionSummaryRouteViaKeyFieldMode.GRAYED_OUT;

/**
 * Returns the raw key value shown in the text field for a route via.
 * Only CMM pre-populates the text field from stored or base key values.
 *
 * @param routeVia - Selected route-via option
 * @param baseValue - Underlying CMM key used for CMM auto-population
 * @param currentKey - Current stored or user-entered key value
 * @returns Display value for the key text field
 */
export const getSubmissionSummaryDisplayKey = (
  routeVia: SubmissionSummaryRouteVia,
  baseValue: string,
  currentKey?: string,
): string => {
  if (isSubmissionSummaryKeyFieldDisabled(routeVia)) {
    return "";
  }

  if (routeVia === SubmissionSummaryRouteVia.CMM) {
    const source = currentKey?.trim() || baseValue.trim();
    return stripRouteViaPrefixFromKey(source);
  }

  return "";
};

/**
 * Returns whether the key text field is required for a route via.
 *
 * @param routeVia - Selected route-via option
 * @returns True when the field is mandatory
 */
export const isSubmissionSummaryKeyFieldRequired = (
  routeVia: SubmissionSummaryRouteVia,
): boolean =>
  SUBMISSION_SUMMARY_ROUTE_VIA_KEY_FIELD_MODE[routeVia] ===
  SubmissionSummaryRouteViaKeyFieldMode.MANDATORY;

/**
 * Resolves the `covermymed_id` value sent on submit for the selected route via.
 *
 * @param routeVia - Selected route-via option
 * @param baseValue - Underlying CMM key used for `{value}` placeholders
 * @param userEnteredKey - Current key text-field value
 * @returns Payload `covermymed_id` value
 */
export const getSubmissionSummaryPayloadKey = (
  routeVia: SubmissionSummaryRouteVia,
  baseValue: string,
  userEnteredKey: string,
): string => {
  if (isSubmissionSummaryKeyFieldDisabled(routeVia)) {
    return buildCoverMyMedsIdForRouteVia(routeVia, baseValue);
  }

  const rawValue = stripRouteViaPrefixFromKey(
    userEnteredKey.trim() || baseValue.trim(),
  );
  return buildCoverMyMedsIdForRouteVia(routeVia, rawValue);
};
